import React, { useEffect } from 'react';
import './About.css';
import { useLanguage } from '../context/LanguageContext';
import Icon from '../components/Icon';

/* Scroll-reveal hook */
const useScrollReveal = (selector, options = {}) => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in-view'); });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px', ...options });
    document.querySelectorAll(selector).forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [selector]);
};

const About = () => {
  const { t } = useLanguage();

  const detailedHistorySections = t('about.historySections', { returnObjects: true }) || [];
  const milestones = t('about.milestones', { returnObjects: true }) || [];

  useScrollReveal('.history-scroll-item');
  useScrollReveal('.milestones-scroll-wrapper');
  useScrollReveal('.cultural-scroll-wrapper');

  return (
    <div className="about-page">

      {/* CINEMATIC HERO */}
      <div className="about-hero">
        <div className="about-hero-bg" />
        <div className="about-hero-overlay" />
        <div className="about-hero-rays" aria-hidden="true">
          {[...Array(5)].map((_, i) => <span key={i} className={`about-ray about-ray-${i + 1}`} />)}
        </div>
        <div className="about-hero-content">
          <span className="about-hero-pill-pill">{t('nav.about')}</span>
          <h1 className="about-hero-title">{t('about.heritageTitle')}</h1>
          <div className="about-hero-divider">
            <span className="ahd-line" />
            <span className="ahd-diamond">◆</span>
            <span className="ahd-line" />
          </div>
        </div>
        <div className="about-hero-bottom-line" />
      </div>

      <div className="container">

        {/* Vision — always visible, no scroll reveal */}
        <section className="vision-intro-section">
          <div className="vision-parchment">
            <div className="vision-content">
              <h2>{t('about.divineOrigin')}</h2>
              <p >{t('about.divineOriginText1')}</p>
              <div className="parchment-divider" />
              <p >{t('about.divineOriginText2')}</p>
            </div>
          </div>
        </section>

        {/* History Grid */}
        <section className="history-grid-section">
          <h2 className="section-title">{t('about.journeyTitle')}</h2>
          <div className="header-divider section-divider">
            <span className="dot" /><span className="line" /><span className="dot" />
          </div>
          <div className="history-grid">
            {detailedHistorySections.map((section, idx) => (
              <div className="history-scroll-item" key={idx}>
                <div className="history-scroll-content">
                  <div className="history-scroll-header">
                    <div className="history-icon"><Icon name={section.icon} /></div>
                    <h3>{section.title}</h3>
                  </div>
                  {section.paragraphs.map((p, pIdx) => <p  key={pIdx} >{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Milestones */}
        <section className="milestones-timeline-section">
          <div className="milestones-scroll-wrapper">
            <div className="milestones-scroll-content">
              <h2 className="milestones-scroll-title">{t('about.milestonesTitle')}</h2>
              <div className="parchment-divider" />
              <div className="milestones-row">
                {milestones.map((m, idx) => (
                  <div className="milestone-item" key={idx}>
                    <div className="milestone-year">{m.year}</div>
                    <div className="milestone-dot" />
                    <p >{m.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cultural Significance */}
        <section className="cultural-significance-section">
          <div className="cultural-scroll-wrapper">
            <div className="cultural-scroll-content">
              <h2 className="cultural-scroll-title">{t('about.culturalImportance')}</h2>
              <div className="parchment-divider" />
              <div className="text-side">
                <p >{t('about.culturalText1')}</p>
                <p >{t('about.culturalText2')}</p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default About;








